import { Container, Graphics } from 'pixi.js';
import { BaseScene } from './BaseScene';
import { Board, BOARD_SIZE } from '../entities/Board';
import { Piece, TILE_SIZE } from '../entities/Piece';
import { MoveSystem } from '../systems/MoveSystem';
import { TurnSystem } from '../systems/TurnSystem';
import { AISystem } from '../systems/AISystem';
import { TurnIndicator } from '../ui/TurnIndicator';
import { GameOverOverlay } from '../ui/GameOverOverlay';
import { PlayerSide } from '../types/game.types';
import type { GridPosition, MoveResult, PieceData } from '../types/game.types';
import type { SceneContext } from '../types/game.types';
import { getDifficulty } from '../state/gameSettings';
import { logger } from '../utils/logger';
import { easeOutCubic } from '../utils/animation';
import { PALETTE } from '../ui/theme';

const TILE_LIGHT = 0x304b40;
const TILE_DARK = 0x233a32;
const MOVE_HINT_COLOR = PALETTE.accent;
const ATTACK_HINT_COLOR = PALETTE.danger;
const HINT_ALPHA = 0.2;

export class GameScene extends BaseScene {
  private board: Board;
  private moveSystem: MoveSystem;
  private turnSystem: TurnSystem;
  private aiSystem: AISystem;

  private gridContainer: Container | null = null;
  private piecesContainer: Container | null = null;
  private hintsContainer: Container | null = null;
  private background: Graphics | null = null;
  private boardFrame: Graphics | null = null;
  private turnIndicator: TurnIndicator | null = null;
  private gameOverOverlay: GameOverOverlay | null = null;
  private sceneTime: number = 0;

  private pieceViews: Map<string, Piece> = new Map();

  private selectedPosition: GridPosition | null = null;
  private isAITurn: boolean = false;
  private isGameOver: boolean = false;

  constructor(context: SceneContext) {
    super(context);
    this.board = new Board();
    this.moveSystem = new MoveSystem();
    this.turnSystem = new TurnSystem();
    this.aiSystem = new AISystem(getDifficulty());
  }

  async load(): Promise<void> {
    await Promise.resolve();
  }

  enter(): void {
    logger.info('Entering GameScene');
    this.sceneTime = 0;
    this.buildScene();
    this.startGame();
  }

  update(delta: number): void {
    this.sceneTime += delta;
    this.animateBackground();
    this.turnIndicator?.animate(delta);
    this.gameOverOverlay?.animate(delta);

    for (const piece of this.pieceViews.values()) {
      piece.animate(delta);
    }
  }

  exit(): void {
    logger.info('Exiting GameScene');
    this.removeChildren();
  }

  unload(): void {
    this.removeChildren();
    this.pieceViews.clear();
  }

  resize(width: number, height: number): void {
    super.resize(width, height);
    this.removeChildren();
    this.pieceViews.clear();
    this.buildScene();
    this.rebuildPieceViews();
  }

  private buildScene(): void {
    const { width, height } = this.context;
    const boardPixelSize = TILE_SIZE * BOARD_SIZE;
    const offsetX = Math.floor((width - boardPixelSize) / 2);
    const offsetY = Math.floor((height - boardPixelSize) / 2);

    this.background = new Graphics();
    this.drawBackground();
    this.addChild(this.background);

    this.boardFrame = new Graphics();
    this.drawBoardFrame(offsetX, offsetY, boardPixelSize);
    this.addChild(this.boardFrame);

    this.gridContainer = new Container();
    this.hintsContainer = new Container();
    this.piecesContainer = new Container();

    this.gridContainer.x = offsetX;
    this.gridContainer.y = offsetY;
    this.hintsContainer.x = offsetX;
    this.hintsContainer.y = offsetY;
    this.piecesContainer.x = offsetX;
    this.piecesContainer.y = offsetY;

    this.addChild(this.gridContainer);
    this.addChild(this.hintsContainer);
    this.addChild(this.piecesContainer);

    this.drawGrid();
    this.setupGridInteraction(boardPixelSize);

    this.turnIndicator = new TurnIndicator();
    this.turnIndicator.x = 24;
    this.turnIndicator.y = 24;
    this.addChild(this.turnIndicator);

    this.gameOverOverlay = new GameOverOverlay(width, height);
    this.gameOverOverlay.onPlayAgain(() => this.resetGame());
    this.addChild(this.gameOverOverlay);
  }

  private drawBackground(): void {
    if (!this.background) return;

    const { width, height } = this.context;

    this.background.clear();
    this.background.beginFill(PALETTE.night);
    this.background.drawRect(0, 0, width, height);
    this.background.endFill();

    this.background.beginFill(PALETTE.forest, 0.28);
    this.background.drawCircle(width * 0.16, height * 0.18, Math.min(width, height) * 0.28);
    this.background.endFill();

    this.background.beginFill(PALETTE.accent, 0.08);
    this.background.drawCircle(width * 0.82, height * 0.2, Math.min(width, height) * 0.24);
    this.background.endFill();

    this.background.beginFill(PALETTE.surfaceSoft, 0.8);
    this.background.drawPolygon([
      0,
      height,
      0,
      height * 0.76,
      width * 0.22,
      height * 0.72,
      width * 0.44,
      height * 0.78,
      width * 0.7,
      height * 0.7,
      width,
      height * 0.76,
      width,
      height,
    ]);
    this.background.endFill();
  }

  private drawBoardFrame(offsetX: number, offsetY: number, boardPixelSize: number): void {
    if (!this.boardFrame) return;

    this.boardFrame.clear();
    this.boardFrame.beginFill(0x000000, 0.2);
    this.boardFrame.drawEllipse(
      offsetX + boardPixelSize / 2,
      offsetY + boardPixelSize + 28,
      boardPixelSize * 0.44,
      28,
    );
    this.boardFrame.endFill();

    this.boardFrame.beginFill(PALETTE.surface, 0.9);
    this.boardFrame.drawRoundedRect(offsetX - 24, offsetY - 24, boardPixelSize + 48, boardPixelSize + 48, 30);
    this.boardFrame.endFill();

    this.boardFrame.lineStyle(1.2, 0xffffff, 0.14);
    this.boardFrame.drawRoundedRect(offsetX - 24, offsetY - 24, boardPixelSize + 48, boardPixelSize + 48, 30);
  }

  private drawGrid(): void {
    if (!this.gridContainer) return;
    this.gridContainer.removeChildren();

    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const color = (row + col) % 2 === 0 ? TILE_LIGHT : TILE_DARK;
        const tile = new Graphics();
        tile.beginFill(color);
        tile.drawRoundedRect(col * TILE_SIZE, row * TILE_SIZE, TILE_SIZE, TILE_SIZE, 12);
        tile.endFill();
        tile.lineStyle(1, 0xffffff, 0.06);
        tile.drawRoundedRect(col * TILE_SIZE + 1, row * TILE_SIZE + 1, TILE_SIZE - 2, TILE_SIZE - 2, 11);
        this.gridContainer.addChild(tile);
      }
    }
  }

  private setupGridInteraction(boardPixelSize: number): void {
    const hitArea = new Graphics();
    hitArea.beginFill(0x000000, 0.001);
    hitArea.drawRect(0, 0, boardPixelSize, boardPixelSize);
    hitArea.endFill();
    hitArea.eventMode = 'static';
    hitArea.cursor = 'pointer';
    hitArea.on('pointerdown', (event) => {
      const local = event.getLocalPosition(hitArea);
      const col = Math.floor(local.x / TILE_SIZE);
      const row = Math.floor(local.y / TILE_SIZE);

      if (row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE) {
        this.handleTileClick({ row, col });
      }
    });

    this.gridContainer?.addChild(hitArea);
  }

  private startGame(): void {
    this.board.initialise();
    this.turnSystem.reset();
    this.selectedPosition = null;
    this.isAITurn = false;
    this.isGameOver = false;
    this.rebuildPieceViews();
    this.turnIndicator?.update(this.turnSystem.currentSide);
    this.gameOverOverlay?.hide();
    this.clearHints();
  }

  private resetGame(): void {
    this.startGame();
  }

  private rebuildPieceViews(): void {
    if (!this.piecesContainer) return;
    this.piecesContainer.removeChildren();
    this.pieceViews.clear();

    for (const pieceData of this.board.getAllPieces()) {
      this.createPieceView(pieceData);
    }
  }

  private createPieceView(pieceData: PieceData): Piece {
    const view = new Piece(pieceData);
    view.x = pieceData.position.col * TILE_SIZE;
    view.y = pieceData.position.row * TILE_SIZE;
    view.on('pointerdown', () => {
      this.handleTileClick(this.getPieceViewPosition(view));
    });
    this.pieceViews.set(pieceData.id, view);
    this.piecesContainer?.addChild(view);
    return view;
  }

  private getPieceViewPosition(view: Piece): GridPosition {
    return {
      row: Math.round(view.y / TILE_SIZE),
      col: Math.round(view.x / TILE_SIZE),
    };
  }

  private handleTileClick(pos: GridPosition): void {
    if (this.isAITurn || this.isGameOver) return;
    if (this.turnSystem.currentSide !== PlayerSide.KIWI) return;

    const clickedPiece = this.board.getPiece(pos);

    if (this.selectedPosition === null) {
      if (clickedPiece && clickedPiece.side === PlayerSide.KIWI) {
        this.selectPiece(pos);
      }
      return;
    }

    const { moveTiles, attackTiles } = this.moveSystem.calculateValidMoves(
      this.board,
      this.selectedPosition,
    );
    const isMoveTile = moveTiles.some((tile) => tile.row === pos.row && tile.col === pos.col);
    const isAttackTile = attackTiles.some((tile) => tile.row === pos.row && tile.col === pos.col);

    if (isMoveTile || isAttackTile) {
      void this.executeMove(this.selectedPosition, pos);
    } else if (clickedPiece && clickedPiece.side === PlayerSide.KIWI) {
      this.selectPiece(pos);
    } else {
      this.deselect();
    }
  }

  private selectPiece(pos: GridPosition): void {
    this.deselect();
    this.selectedPosition = pos;

    const piece = this.board.getPiece(pos);
    if (piece) {
      const view = this.pieceViews.get(piece.id);
      if (view) view.isSelected = true;
    }

    const { moveTiles, attackTiles } = this.moveSystem.calculateValidMoves(this.board, pos);
    this.drawHints(moveTiles, attackTiles);
  }

  private deselect(): void {
    if (this.selectedPosition !== null) {
      const piece = this.board.getPiece(this.selectedPosition);
      if (piece) {
        const view = this.pieceViews.get(piece.id);
        if (view) view.isSelected = false;
      }
      this.selectedPosition = null;
    }
    this.clearHints();
  }

  private async executeMove(from: GridPosition, to: GridPosition): Promise<void> {
    const movingPiece = this.board.getPiece(from);
    if (!movingPiece) return;

    const capturedPiece = this.board.getPiece(to);

    this.deselect();

    if (capturedPiece) {
      const capturedView = this.pieceViews.get(capturedPiece.id);
      if (capturedView) {
        await capturedView.flashCapture();
        this.piecesContainer?.removeChild(capturedView);
        this.pieceViews.delete(capturedPiece.id);
        this.board.removePiece(to);
      }
    }

    this.board.removePiece(from);
    const updatedPiece: PieceData = { ...movingPiece, position: { ...to } };
    this.board.setPiece(to, updatedPiece);

    const view = this.pieceViews.get(movingPiece.id);
    if (view) {
      await this.animatePieceMovement(view, to);
      view.pieceData.position = { ...to };
    }

    const winner = this.turnSystem.checkWinCondition(this.board);
    if (winner !== null) {
      this.endGame(winner);
      return;
    }

    this.turnSystem.switchTurn();
    this.turnIndicator?.update(this.turnSystem.currentSide);

    if (this.turnSystem.currentSide === PlayerSide.COCONUT) {
      this.isAITurn = true;
      const result = await this.aiSystem.takeTurn(this.board, this.moveSystem);
      this.isAITurn = false;
      if (result) {
        await this.applyAIMove(result);
      }
    }
  }

  private async applyAIMove(result: MoveResult): Promise<void> {
    if (result.capturedPiece) {
      const capturedView = this.pieceViews.get(result.capturedPiece.id);
      if (capturedView) {
        await capturedView.flashCapture();
        this.piecesContainer?.removeChild(capturedView);
        this.pieceViews.delete(result.capturedPiece.id);
      }
    }

    const view = this.pieceViews.get(result.movedPiece.id);
    if (view) {
      await this.animatePieceMovement(view, result.toPosition);
      view.pieceData.position = { ...result.toPosition };
    }

    const winner = this.turnSystem.checkWinCondition(this.board);
    if (winner !== null) {
      this.endGame(winner);
      return;
    }

    this.turnSystem.switchTurn();
    this.turnIndicator?.update(this.turnSystem.currentSide);
  }

  private endGame(winner: PlayerSide): void {
    this.isGameOver = true;
    logger.info('Game over! Winner:', winner);
    this.gameOverOverlay?.show(winner);
  }

  private drawHints(moveTiles: GridPosition[], attackTiles: GridPosition[]): void {
    this.clearHints();
    if (!this.hintsContainer) return;

    for (const pos of moveTiles) {
      const hint = new Graphics();
      hint.beginFill(MOVE_HINT_COLOR, HINT_ALPHA);
      hint.drawRoundedRect(pos.col * TILE_SIZE + 6, pos.row * TILE_SIZE + 6, TILE_SIZE - 12, TILE_SIZE - 12, 16);
      hint.endFill();
      hint.lineStyle(2, MOVE_HINT_COLOR, 0.9);
      hint.drawCircle(
        pos.col * TILE_SIZE + TILE_SIZE / 2,
        pos.row * TILE_SIZE + TILE_SIZE / 2,
        18,
      );
      hint.beginFill(MOVE_HINT_COLOR, 0.95);
      hint.drawCircle(
        pos.col * TILE_SIZE + TILE_SIZE / 2,
        pos.row * TILE_SIZE + TILE_SIZE / 2,
        6,
      );
      hint.endFill();
      this.hintsContainer.addChild(hint);
    }

    for (const pos of attackTiles) {
      const hint = new Graphics();
      hint.beginFill(ATTACK_HINT_COLOR, HINT_ALPHA);
      hint.drawRoundedRect(pos.col * TILE_SIZE + 5, pos.row * TILE_SIZE + 5, TILE_SIZE - 10, TILE_SIZE - 10, 16);
      hint.endFill();
      hint.lineStyle(2, ATTACK_HINT_COLOR, 0.9);
      hint.drawRoundedRect(pos.col * TILE_SIZE + 8, pos.row * TILE_SIZE + 8, TILE_SIZE - 16, TILE_SIZE - 16, 14);
      hint.moveTo(pos.col * TILE_SIZE + 22, pos.row * TILE_SIZE + 22);
      hint.lineTo(pos.col * TILE_SIZE + TILE_SIZE - 22, pos.row * TILE_SIZE + TILE_SIZE - 22);
      hint.moveTo(pos.col * TILE_SIZE + TILE_SIZE - 22, pos.row * TILE_SIZE + 22);
      hint.lineTo(pos.col * TILE_SIZE + 22, pos.row * TILE_SIZE + TILE_SIZE - 22);
      this.hintsContainer.addChild(hint);
    }
  }

  private clearHints(): void {
    this.hintsContainer?.removeChildren();
  }

  private animateBackground(): void {
    if (!this.background) return;

    this.drawBackground();

    const { width, height } = this.context;
    const glowRadius = Math.min(width, height) * 0.24;
    const drift = Math.sin(this.sceneTime * 0.35) * 22;

    this.background.beginFill(PALETTE.kiwi, 0.05);
    this.background.drawCircle(width * 0.18 + drift, height * 0.2, glowRadius);
    this.background.endFill();

    this.background.beginFill(PALETTE.accent, 0.04);
    this.background.drawCircle(width * 0.82 - drift, height * 0.16, glowRadius * 0.9);
    this.background.endFill();
  }

  private animatePieceMovement(view: Piece, to: GridPosition): Promise<void> {
    const fromX = view.x;
    const fromY = view.y;
    const targetX = to.col * TILE_SIZE;
    const targetY = to.row * TILE_SIZE;
    const startedAt = performance.now();
    const duration = 220;

    return new Promise((resolve) => {
      const step = (): void => {
        const progress = Math.min((performance.now() - startedAt) / duration, 1);
        const eased = easeOutCubic(progress);

        view.x = fromX + (targetX - fromX) * eased;
        view.y = fromY + (targetY - fromY) * eased;

        if (progress >= 1) {
          resolve();
          return;
        }

        requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    });
  }
}
